import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as CompanyIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  AccountBalanceWallet as WalletIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

const currency = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ─────────────────────────────────────────────────────────────────────────────
// EnhancedPayoutCard
//
// Claim flow (simplified):
//   • Instant  → always onClaimToBalance. Creator withdraws from wallet later.
//   • Net 30   → onClaimNet30 (or onClaimToBalance as fallback).
//   • Campaign → onApproveCampaignPayout(id, 'BLITZPAY' | 'NET30').
//
// The old "destination" dialog (balance vs withdraw) and "forced rail" dialog
// have been removed. Instant always credits balance; complexity lives in the
// wallet/withdrawal section, not here.
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedPayoutCard = ({
  payout,
  onClaimToBalance,
  onClaimWithWithdrawal, // kept in prop signature for backward-compat; not used in claim flow
  onClaimNet30,
  onApproveCampaignPayout,
  wallets,
  claimingPayoutId,
  formatItemDate,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [timingDialogOpen, setTimingDialogOpen] = useState(false);
  const [selectedTiming, setSelectedTiming] = useState('instant');

  // ── Derived values ────────────────────────────────────────────────────────
  const grossAmount      = Number(payout.amount || 0);
  const isClientPaidFee  = payout.role === 'Client';

  const _src         = String(payout.source_type || '').toLowerCase();
  const _isCampaign  = ['managed', 'direct_chat', 'campaign'].includes(_src);
  const _isPartnership = _src === 'partnership';
  let _instantPct = 0;
  if (_isCampaign || _isPartnership) _instantPct = 0.05;
  else if (!isClientPaidFee)         _instantPct = 0.05;
  const instantFee  = grossAmount * _instantPct;
  const instantNet  = grossAmount - instantFee;
  const net30Amount = grossAmount;

  // ── Status flags ──────────────────────────────────────────────────────────
  const normalizedStatus = String(payout.status || '').trim().toLowerCase();
  const isApproved   = normalizedStatus === 'approved';
  const isFunded     = normalizedStatus === 'funded';
  const isPending    = normalizedStatus === 'pending';
  const isCompleted  = ['paid', 'completed', 'confirmed', 'funded'].includes(normalizedStatus);
  const isQueuedForManualProcessing = payout.withdrawal?.processing_mode === 'manual_fallback';
  const isCampaignPayout     = !!payout.campaign_id;
  const isCampaignApproveFlow = isCampaignPayout && typeof onApproveCampaignPayout === 'function';

  // Auto-credited payouts (funded + blitzpay=true, no withdrawal_id needed — already in balance)
  const isAutoCredited = isFunded && payout.blitzpay === true && !payout.withdrawal_id;

  const isClaimable =
    (!payout.withdrawal_id && isApproved && !isCampaignPayout) ||
    (!payout.withdrawal_id && isPending  && isCampaignPayout);

  const showClaimActions = isCampaignApproveFlow ? isPending : isClaimable;
  const needsAction      = isCampaignPayout      ? isPending : isClaimable;

  const isMerged    = payout.isMerged === true;
  const invoiceId   = payout.linkedInvoiceId;

  // ── Display helpers ───────────────────────────────────────────────────────
  const displayDate = formatItemDate
    ? formatItemDate(payout)
    : (() => {
        for (const raw of [payout.payout_date, payout.created_at, payout.date]) {
          if (!raw) continue;
          const d = new Date(raw);
          if (!isNaN(d.getTime()))
            return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        }
        return null;
      })();

  const getSourceInfo = () => {
    let companyName = null;
    let personName  = null;
    if (payout.company?.name)                companyName = payout.company.name;
    else if (payout.transaction?.company?.name) companyName = payout.transaction.company.name;
    else if (payout.user?.company_name)      companyName = payout.user.company_name;
    else if (payout.client_name)             companyName = payout.client_name;
    else if (payout.invoice?.client_name)    companyName = payout.invoice.client_name;

    if (payout.user?.username)                        personName = payout.user.username;
    else if (payout.user?.first_name && payout.user?.last_name)
      personName = `${payout.user.first_name} ${payout.user.last_name}`;
    else if (payout.transaction?.user?.username)      personName = payout.transaction.user.username;

    if (companyName && personName) return { company: companyName, person: personName };
    if (companyName)               return { company: companyName, person: null };
    if (personName)                return { company: personName,  person: null };
    return { company: payout.po_number ? `PO: ${payout.po_number}` : `Payment #${payout.id}`, person: null };
  };

  const sourceInfo = getSourceInfo();
  const poNumber   = payout.po_number || payout.unique_code || `#${payout.id}`;
  const notes      = payout.notes || payout.description || '';

  // ── Completed amount ──────────────────────────────────────────────────────
  const getActualReceivedAmount = () => {
    if (!isCompleted) return null;
    if (isAutoCredited) return instantNet; // auto-credited net (fees already deducted)
    if (payout.blitzpay) return isClientPaidFee ? grossAmount : instantNet;
    return net30Amount;
  };
  const actualReceived = getActualReceivedAmount();

  // ── Timing dialog ─────────────────────────────────────────────────────────
  const getRecommendedTiming = () => {
    if (isClientPaidFee)    return 'instant';
    if (grossAmount < 1000) return 'instant';
    return 'net30';
  };

  const handleOpenTimingDialog = () => {
    setSelectedTiming(getRecommendedTiming());
    setTimingDialogOpen(true);
  };

  const handleConfirmTiming = () => {
    setTimingDialogOpen(false);
    if (selectedTiming === 'instant') {
      // Always credit balance — creator withdraws from wallet settings later
      if (isCampaignApproveFlow) {
        onApproveCampaignPayout(payout.id, 'BLITZPAY');
      } else {
        onClaimToBalance(payout.id);
      }
    } else {
      // Net 30
      if (isCampaignApproveFlow) {
        onApproveCampaignPayout(payout.id, 'NET30');
      } else {
        if (onClaimNet30) onClaimNet30(payout.id);
        else onClaimToBalance(payout.id);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: needsAction ? 'primary.main' : 'divider',
          borderWidth: needsAction ? 2 : 1,
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: needsAction ? 4 : 2 },
        }}
      >
        {/* Action / funded badges */}
        {(needsAction || isAutoCredited) && (
          <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
            {needsAction && (
              <Chip label="Action Required" color="primary" size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
            )}
            {isAutoCredited && (
              <Chip
                label="Auto-credited to balance"
                color="success"
                size="small"
                icon={<CheckIcon sx={{ fontSize: 14 }} />}
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>
        )}

        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* ── Header ── */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <CompanyIcon sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
                <Typography variant="h6" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sourceInfo.company}
                </Typography>
              </Box>
              {!needsAction && (
                <Chip
                  label={isAutoCredited ? 'Funded' : payout.status}
                  color={isCompleted ? 'success' : isPending ? 'warning' : 'info'}
                  size="small"
                  sx={{ fontSize: '0.75rem', height: 24, textTransform: 'capitalize', fontWeight: 500 }}
                />
              )}
            </Box>

            {sourceInfo.person && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4.5, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{sourceInfo.person}</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, ml: 4.5 }}>
              <Typography variant="caption" color="text.secondary">PO: {poNumber}</Typography>
              {displayDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">{displayDate}</Typography>
                </Box>
              )}
            </Box>

            {isMerged && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, ml: 4.5 }}>
                <LinkIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">Invoice &amp; Payout combined</Typography>
                {invoiceId && (
                  <Chip
                    icon={<ReceiptIcon sx={{ fontSize: '12px !important' }} />}
                    label={`INV-${invoiceId}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* ── Amount block ── */}
          <Box
            sx={{
              bgcolor: isCompleted
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.08),
              p: 2.5,
              borderRadius: 2,
              mb: 2.5,
              border: 1,
              borderColor: isCompleted
                ? alpha(theme.palette.success.main, 0.2)
                : alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Stack spacing={1.5}>
              {isCompleted && actualReceived !== null ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {isAutoCredited
                        ? 'Credited to Balance'
                        : payout.blitzpay
                        ? 'Received (Instant)'
                        : 'Received (Net 30)'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {currency(actualReceived)}
                    </Typography>
                  </Box>
                  {(isAutoCredited || payout.blitzpay) && !isClientPaidFee && instantFee > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Original Amount</Typography>
                        <Typography variant="body2">{currency(grossAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {isAutoCredited ? 'Platform Fee' : 'Instant Fee'} ({Math.round(_instantPct * 100)}%)
                        </Typography>
                        <Typography variant="body2" color="warning.main" fontWeight={500}>-{currency(instantFee)}</Typography>
                      </Box>
                    </>
                  )}
                  {isClientPaidFee && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label="Client Paid All Fees" color="success" size="small" icon={<CheckIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.75rem', height: 24, fontWeight: 500 }} />
                    </Box>
                  )}
                  {isAutoCredited && (
                    <Alert severity="success" sx={{ mt: 1, borderRadius: 1.5, py: 0.5 }}>
                      <Typography variant="caption">
                        Funds are in your Blitz balance. Withdraw anytime from your wallet settings.
                      </Typography>
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Payment Amount</Typography>
                    <Typography variant="h5" fontWeight={700}>{currency(grossAmount)}</Typography>
                  </Box>
                  {!isClientPaidFee && !isCompleted && instantFee > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Instant Fee ({Math.round(_instantPct * 100)}%)</Typography>
                          <Typography variant="body2" color="warning.main" fontWeight={500}>-{currency(instantFee)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>Instant Net</Typography>
                          <Typography variant="body1" fontWeight={600}>{currency(instantNet)}</Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="success.main" fontWeight={500}>Net 30 (No Fee)</Typography>
                          <Typography variant="body1" fontWeight={600} color="success.main">{currency(net30Amount)}</Typography>
                        </Box>
                      </Stack>
                    </>
                  )}
                  {isClientPaidFee && !isCompleted && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="Client Paid All Fees" color="success" size="small" icon={<CheckIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.75rem', height: 24, fontWeight: 500 }} />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>

          {/* ── Notes ── */}
          {notes && (
            <Box>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto', mb: 1, '&:hover': { bgcolor: 'transparent' } }}
              >
                <Typography variant="body2" color="primary" fontWeight={500}>
                  {expanded ? 'Hide' : 'Show'} Details
                </Typography>
              </Button>
              <Collapse in={expanded}>
                <Box sx={{ mt: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 1, borderLeft: 3, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{notes}</Typography>
                </Box>
              </Collapse>
            </Box>
          )}

          {isQueuedForManualProcessing && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
              <Typography variant="body2">
                This payout has been queued for manual admin processing. Funds will not be auto-sent until an admin completes the payout.
              </Typography>
            </Alert>
          )}
        </CardContent>

        {/* ── Actions ── */}
        <CardActions
          sx={{
            p: 3,
            pt: 0,
            mt: 'auto',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
            width: '100%',
            '& > *': { width: '100%', maxWidth: '100%', flex: '0 0 auto' },
          }}
        >
          {showClaimActions && (
            <Stack spacing={1.5} width="100%">
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleOpenTimingDialog}
                disabled={claimingPayoutId === payout.id}
                startIcon={<SpeedIcon />}
                sx={{ py: 1.5, fontWeight: 600, fontSize: '0.9375rem' }}
              >
                {claimingPayoutId === payout.id
                  ? 'Processing…'
                  : isCampaignApproveFlow
                  ? 'Approve Payout'
                  : 'Claim Payment'}
              </Button>

              {/* Contextual sub-label */}
              {isClientPaidFee && (
                <Typography variant="caption" color="success.main" fontWeight={600} sx={{ textAlign: 'center' }}>
                  ✓ Client paid all fees — you receive the full amount
                </Typography>
              )}
              {!isClientPaidFee && instantFee > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Instant adds to your Blitz balance (fees apply) · Net 30 = no fees
                </Typography>
              )}
            </Stack>
          )}

          {isPending && !isCampaignPayout && (
            <Box sx={{ width: '100%', textAlign: 'center', py: 1, px: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Awaiting approval from {sourceInfo.company}
              </Typography>
            </Box>
          )}

          {isCampaignApproveFlow && isApproved && !isCompleted && (
            <Box sx={{ width: '100%', textAlign: 'center', py: 1.5, px: 2, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Awaiting admin confirmation — your payout timing is saved.
              </Typography>
            </Box>
          )}

          {isCompleted && (
            <Chip
              label={
                isAutoCredited
                  ? 'Credited to Balance'
                  : payout.blitzpay
                  ? 'Paid (Instant)'
                  : 'Paid (Net 30)'
              }
              color="success"
              icon={<CheckIcon />}
              sx={{ width: '100%', fontSize: '0.875rem', height: 36, fontWeight: 600 }}
            />
          )}
        </CardActions>
      </Card>

      {/* ── Timing Dialog ── */}
      <Dialog
        open={timingDialogOpen}
        onClose={() => setTimingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" fontWeight={600}>Choose Payment Timing</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            Select when you'd like to receive your {currency(grossAmount)} payment.
            {!isCampaignApproveFlow && (
              <> Instant payments are added to your <strong>Blitz balance</strong> — withdraw to your payment method anytime from your wallet settings.</>
            )}
          </Typography>

          <RadioGroup value={selectedTiming} onChange={(e) => setSelectedTiming(e.target.value)}>
            {/* Instant option */}
            <Card
              variant="outlined"
              onClick={() => setSelectedTiming('instant')}
              sx={{
                p: 2.5, mb: 2, cursor: 'pointer', border: 2,
                borderColor: selectedTiming === 'instant' ? 'primary.main' : 'divider',
                bgcolor: selectedTiming === 'instant' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              <FormControlLabel
                value="instant"
                control={<Radio />}
                sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                label={
                  <Box sx={{ ml: 1.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="h6" fontWeight={600}>⚡ Instant</Typography>
                      <Typography variant="h5" fontWeight={700} color="primary.main">{currency(instantNet)}</Typography>
                    </Box>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">Added to your Blitz balance immediately</Typography>
                      {!isClientPaidFee && (
                        <Typography variant="body2" color="warning.main" fontWeight={500}>
                          {currency(instantFee)} fee ({Math.round(_instantPct * 100)}%)
                        </Typography>
                      )}
                      {isClientPaidFee && (
                        <Typography variant="body2" color="success.main" fontWeight={500}>
                          No fees — client paid all costs
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                }
              />
            </Card>

            {/* Net 30 option */}
            <Card
              variant="outlined"
              onClick={() => setSelectedTiming('net30')}
              sx={{
                p: 2.5, cursor: 'pointer', border: 2,
                borderColor: selectedTiming === 'net30' ? 'success.main' : 'divider',
                bgcolor: selectedTiming === 'net30' ? alpha(theme.palette.success.main, 0.04) : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'success.main', bgcolor: alpha(theme.palette.success.main, 0.04) },
              }}
            >
              <FormControlLabel
                value="net30"
                control={<Radio />}
                sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                label={
                  <Box sx={{ ml: 1.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="h6" fontWeight={600}>📅 Net 30</Typography>
                      <Typography variant="h5" fontWeight={700} color="success.main">{currency(net30Amount)}</Typography>
                    </Box>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">30 days</Typography>
                      <Typography variant="body2" color="success.main" fontWeight={500}>
                        No fees • Save {currency(instantFee)}
                      </Typography>
                    </Stack>
                  </Box>
                }
              />
            </Card>
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
          <Button onClick={() => setTimingDialogOpen(false)} size="large" sx={{ px: 3 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmTiming}
            disabled={!selectedTiming}
            size="large"
            sx={{ px: 3, fontWeight: 600 }}
          >
            Confirm {selectedTiming === 'instant' ? 'Instant' : 'Net 30'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnhancedPayoutCard;


// ─────────────────────────────────────────────────────────────────────────────
// PaymentTimingSelector — standalone component used on the claim page
// ─────────────────────────────────────────────────────────────────────────────
export const PaymentTimingSelector = ({
  grossAmount,
  isClientPaidFee,
  selectedTiming,
  onTimingChange,
}) => {
  const theme    = useTheme();
  const instantFee  = isClientPaidFee ? 0 : grossAmount * 0.05;
  const instantNet  = grossAmount - instantFee;
  const net30Amount = grossAmount;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SpeedIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight={600}>Choose Your Payment Speed</Typography>
      </Box>

      {isClientPaidFee && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
            <strong>Great news!</strong> The client paid all processing fees. Both options cost you nothing.
          </Typography>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          Instant payments are added directly to your <strong>Blitz balance</strong>.
          You can withdraw to your payment method (PayPal, Stripe, ACH) anytime from your wallet settings.
        </Typography>
      </Alert>

      <RadioGroup value={selectedTiming} onChange={(e) => onTimingChange(e.target.value)}>
        {/* Instant */}
        <Card
          variant="outlined"
          onClick={() => onTimingChange('instant')}
          sx={{
            p: 3.5, mb: 2.5, cursor: 'pointer', border: 2,
            borderColor: selectedTiming === 'instant' ? 'primary.main' : 'divider',
            bgcolor: selectedTiming === 'instant' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'translateY(-2px)', boxShadow: 2 },
          }}
        >
          <FormControlLabel
            value="instant"
            control={<Radio sx={{ mt: -0.5 }} />}
            sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
            label={
              <Box sx={{ ml: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" fontWeight={600}>⚡ Instant Payment</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary.main">{currency(instantNet)}</Typography>
                </Box>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckIcon fontSize="small" color="success" />
                    <Typography variant="body1">Added to your Blitz balance immediately</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <WalletIcon fontSize="small" color="primary" />
                    <Typography variant="body1">Withdraw to PayPal, Stripe, or ACH anytime</Typography>
                  </Box>
                  {!isClientPaidFee ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InfoIcon fontSize="small" color="warning" />
                        <Typography variant="body1">{currency(instantFee)} processing fee (5%)</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 4, lineHeight: 1.6 }}>
                        {currency(grossAmount)} gross − {currency(instantFee)} fee = {currency(instantNet)} net
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckIcon fontSize="small" color="success" />
                      <Typography variant="body1" color="success.main" fontWeight={500}>No fees — client paid all costs</Typography>
                    </Box>
                  )}
                </Stack>
                {selectedTiming === 'instant' && (
                  <Alert severity="info" sx={{ mt: 2.5, borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {isClientPaidFee
                        ? '✓ Best choice! Get credited immediately with no deductions.'
                        : grossAmount < 500
                        ? '✓ Good choice for amounts under $500 where speed matters.'
                        : `⚠ For ${currency(grossAmount)}, consider Net 30 to save ${currency(instantFee)}.`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            }
          />
        </Card>

        {/* Net 30 */}
        <Card
          variant="outlined"
          onClick={() => onTimingChange('net30')}
          sx={{
            p: 3.5, cursor: 'pointer', border: 2,
            borderColor: selectedTiming === 'net30' ? 'success.main' : 'divider',
            bgcolor: selectedTiming === 'net30' ? alpha(theme.palette.success.main, 0.04) : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: 'success.main', bgcolor: alpha(theme.palette.success.main, 0.04), transform: 'translateY(-2px)', boxShadow: 2 },
          }}
        >
          <FormControlLabel
            value="net30"
            control={<Radio sx={{ mt: -0.5 }} />}
            sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
            label={
              <Box sx={{ ml: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" fontWeight={600}>📅 Standard Payment (Net 30)</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">{currency(net30Amount)}</Typography>
                </Box>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body1">Arrives in 30–90 business days</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckIcon fontSize="small" color="success" />
                    <Typography variant="body1" color="success.main" fontWeight={500}>Zero processing fees</Typography>
                  </Box>
                  {!isClientPaidFee && instantFee > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <MoneyIcon fontSize="small" color="success" />
                      <Typography variant="body1" color="success.main" fontWeight={600}>
                        Save {currency(instantFee)} vs instant
                      </Typography>
                    </Box>
                  )}
                </Stack>
                {selectedTiming === 'net30' && (
                  <Alert severity="success" sx={{ mt: 2.5, borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      ✓ You'll receive the full {currency(net30Amount)} with no deductions.
                      {!isClientPaidFee && ` That's ${currency(instantFee)} more than instant.`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            }
          />
        </Card>
      </RadioGroup>

      {/* Side-by-side comparison table */}
      <Card variant="outlined" sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.grey[500], 0.03), borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2.5 }}>
          📊 Side-by-Side Comparison
        </Typography>
        <Table size="medium">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', py: 1.5, border: 'none' }}>Timing</TableCell>
              <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                <Chip label="Immediate" color="primary" size="small" sx={{ fontWeight: 500 }} />
              </TableCell>
              <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                <Chip label="30–90 days" color="default" size="small" sx={{ fontWeight: 500 }} />
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.02) }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', py: 1.5, borderRadius: '8px 0 0 8px' }}>You Receive</TableCell>
              <TableCell align="center" sx={{ py: 1.5 }}>
                <Typography variant="body1" fontWeight={600}>{currency(instantNet)}</Typography>
              </TableCell>
              <TableCell align="center" sx={{ py: 1.5, borderRadius: '0 8px 8px 0' }}>
                <Typography variant="body1" fontWeight={600} color="success.main">{currency(net30Amount)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', py: 1.5, border: 'none' }}>Destination</TableCell>
              <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                <Chip label="Blitz Balance" color="primary" size="small" variant="outlined" sx={{ fontWeight: 500 }} />
              </TableCell>
              <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                <Chip label="Blitz Balance" color="default" size="small" variant="outlined" sx={{ fontWeight: 500 }} />
              </TableCell>
            </TableRow>
            {!isClientPaidFee && instantFee > 0 && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', py: 1.5, border: 'none' }}>Fees</TableCell>
                <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                  <Typography variant="body1" color="warning.main" fontWeight={500}>{currency(instantFee)}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5, border: 'none' }}>
                  <Typography variant="body1" color="success.main" fontWeight={600}>$0.00</Typography>
                </TableCell>
              </TableRow>
            )}
            {isClientPaidFee && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', py: 1.5, border: 'none' }}>Fees</TableCell>
                <TableCell align="center" colSpan={2} sx={{ py: 1.5, border: 'none' }}>
                  <Chip label="Client paid all fees" color="success" size="small" icon={<CheckIcon />} sx={{ fontWeight: 500 }} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {!isClientPaidFee && (
        <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>Which should you choose?</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                Choose <strong>Instant</strong> if you need the funds now — they land in your Blitz balance right away.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                Choose <strong>Net 30</strong> if you can wait and want to maximize your earnings with zero fees.
              </Typography>
            </li>
          </Box>
        </Alert>
      )}
    </Box>
  );
};